import type { CSSProperties, ReactNode } from 'react'
import type { GccApiTrace } from '@shared/types'
import type { GccTraceCalculation } from './gccTraceCalculations'

// ── API Trace Panel ──────────────────────────────────────────────────────────
// Executive look (WI 2487): the gx-trace-* rules in gcc-executive.css colour it
// from the palette the design dashboard sets on its .gcc-exec container.

export const RESPONSE_NOT_CAPTURED = 'Response not captured for this run — regenerate to view it.'

const fmtMs = (ms: number) => ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
const fmtTokens = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)

function TraceText({ label, text }: { label?: string; text: string }) {
  return (
    <div className="gx-trace-text">
      {label && <p className="gx-trace-label">{label}</p>}
      <pre className="gx-trace-pre">{text}</pre>
    </div>
  )
}

function TraceSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details className="gx-trace-sec">
      <summary>{title}</summary>
      {children}
    </details>
  )
}

/** What the pipeline calculated in code after this AI call (WI 2323) — shown so every figure traces back to its inputs. */
function TraceCalculation({ calc }: { calc: GccTraceCalculation }) {
  return (
    <TraceSection title="Calculated in code">
      <div className="gx-trace-calc">
        <p className="gx-trace-formula">{calc.formula}</p>
        <dl className="gx-trace-meta">
          {calc.rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </TraceSection>
  )
}

interface Props {
  trace: GccApiTrace[]
  /** Code-side calculations keyed by pipeline step; shown under the first call of that step */
  calculations?: Partial<Record<number, GccTraceCalculation>>
  onClose: () => void
}

export default function GccTracePanel({ trace, calculations = {}, onClose }: Props) {
  const firstIndexOfStep = (step: number) => trace.findIndex(t => t.step === step)
  return (
    <aside className="gx-trace" aria-label="Pipeline trace">
      <div className="gx-trace-head">
        <div>
          <p className="gx-trace-eyebrow">Pipeline Traceability</p>
          <h3 className="gx-trace-title">Pipeline Trace</h3>
          <p className="gx-trace-count">{trace.length} AI call{trace.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={onClose} aria-label="Close pipeline trace" className="gx-trace-close">✕</button>
      </div>
      <ol className="gx-trace-list">
        {trace.map((t, i) => {
          const calc = firstIndexOfStep(t.step) === i ? calculations[t.step] : undefined
          return (
          <li key={i} className="gx-trace-card" style={{ '--d': `${Math.min(i, 8) * 0.04}s` } as CSSProperties}>
            <div className="gx-trace-card-head">
              <div className="min-w-0">
                {/* Number by call position, not t.step: pipeline step 4 makes two calls (GCC-eligible + Retained) that both record step 4. */}
                <span className="gx-trace-step">Step {i}</span>
                <p className="gx-trace-step-label">{t.stepLabel}</p>
              </div>
              <span className="gx-trace-dur">{fmtMs(t.durationMs)}</span>
            </div>
            <dl className="gx-trace-meta gx-trace-meta-2">
              <div><dt>Model</dt> <dd>{t.model}</dd></div>
              <div><dt>Provider</dt> <dd>{t.provider}</dd></div>
              <div><dt>In tokens</dt> <dd>{fmtTokens(t.inputTokens)}</dd></div>
              <div><dt>Out tokens</dt> <dd>{fmtTokens(t.outputTokens)}</dd></div>
              <div><dt>Prompt</dt> <dd>{fmtTokens(t.promptChars)} chars</dd></div>
              <div><dt>Response</dt> <dd>{fmtTokens(t.responseChars)} chars</dd></div>
            </dl>
            <TraceSection title="Input">
              {t.systemPrompt && <TraceText label="System prompt" text={t.systemPrompt} />}
              <TraceText label="Step prompt" text={t.promptPreview || '—'} />
            </TraceSection>
            <TraceSection title="Output">
              {t.response
                ? <TraceText text={t.response} />
                : <p className="gx-trace-missing">{RESPONSE_NOT_CAPTURED}</p>}
            </TraceSection>
            {calc && <TraceCalculation calc={calc} />}
          </li>
          )
        })}
      </ol>
    </aside>
  )
}
